import { useState, useRef, useEffect, useCallback } from "react";
import { useSessionStore } from "@/features/tutor/store/useSessionStore";
import { useUIStore } from "@/features/tutor/store/useUIStore";
import AudioVisualizer from "./AudioVisualizer/AudioVisualizer";
import ChannelToggles from "./ChannelToggles";
import { useChatSettingsStore } from "@/features/tutor/store/useChatSettingsStore";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import Slide from "./Slide";
import NextBiteButton from "./buttons/NextBiteButton";
import PreviousBiteButton from "./buttons/PreviousBiteButton";
import { ScratchPadPanel } from "./scratchpad/ScratchPadPanel";
import { TldrawCanvasPanel } from "@/features/notepad/components/PersistentTldrawCanvasPanel";
import {
  useNotepadStore,
  selectIsNotepadNonInteractive,
} from "@/features/notepad/store/useNotepadStore";
import { TutorMode } from "@/constants/mode";
import { useViewport } from "@/hooks/useViewport";
import {
  StudentInteractionTypes,
  trackStudentInteraction,
} from "../../../utils/mixpanel-utils";
import { SCRATCHPAD_WIDTH } from "../constants";
import { CANVAS_GATEWAY_CONFIGURED } from "@/flags";
import { OpenNotepadToast } from "@/features/notepad/components/OpenNotepadToast";
import { CloseNotepadToast } from "@/features/notepad/components/CloseNotepadToast";
import FixNamePronunciationToast from "@/features/tutor/components/connection/FixNamePronunciationToast";
import QuickNamePronunciationModal from "@/components/QuickNamePronunciationModal";
import { useNameMentionDetector } from "@/features/tutor/hooks/useNameMentionDetector";
import MicSilenceToast from "./MicSilenceToast";
import NotificationStack from "@/components/notifications/NotificationStack";
import {
  NOTIFICATION_STACKS,
  NotificationStackId,
} from "@/constants/notification";
import { AnimatePresence, LazyMotion, domAnimation, m } from "framer-motion";
import { ASSESSMENT_KEYS } from "@/features/tutor/constants/assessmentTypes";

// Blob dimensions (must match AudioVisualizer h/w props)
const BLOB_W = 260;
const BLOB_H = 140;

const ASSESSMENT_TYPES = new Set(ASSESSMENT_KEYS);

const getSlideType = (slide) =>
  slide?.metadata?.slide_type ?? slide?.slide_type ?? null;

const hasNextContentSlide = (slides, fromIndex) => {
  for (let i = fromIndex + 1; i < slides.length; i++) {
    if (!ASSESSMENT_TYPES.has(getSlideType(slides[i]))) return true;
  }
  return false;
};

const Screen = ({
  mode = TutorMode.PUBLIC,
  isCanvasOpen = false,
  canvasProps = null,
  onScratchpadPendingChange,
  scratchpadSubmitRef,
  scratchpadResetPendingRef,
  scratchpadCanvasExportRef,
  scratchpadImageItemsRef,
  scratchpadClearImageBytesRef,
  scratchpadRemoveImageByIdRef,
  onScratchpadInteract,
  onCameraClick,
  pendingImageB64 = null,
  onImagePlaced,
  onScratchpadImageCountChange,
  onMicDeviceConfirmed,
  onNamePronunciationPause,
  onNamePronunciationResume,
}) => {
  const { currentScreen, sessionEnded, sessionId } = useSessionStore();
  const slides = useSlideStore((state) => state.slides);
  const currentSlideIndex = useSlideStore((state) => state.currentSlideIndex);
  const currentLessonId = useSlideStore((state) => state.currentLessonId);
  const [minimized, setMinimized] = useState(false);
  const minimizedRef = useRef(minimized);
  useEffect(() => {
    minimizedRef.current = minimized;
  }, [minimized]);
  const { isMobile, isMobileLandscape, isMobilePortrait } = useViewport();
  const isMobileAny = isMobile || isMobileLandscape;
  const blobScale = isMobileAny ? 0.5 : 1;
  const effectiveBlobScale = isCanvasOpen
    ? (minimized ? 0.5 : 1) * blobScale
    : minimized || isMobileAny
      ? 0.5
      : 1;
  const isScratchPadOpen = useUIStore((state) => state.isScratchPadOpen);
  const lastAgentResponse = useUIStore((state) => state.lastAgentResponse);
  const currentCaptions = useUIStore((state) => state.currentCaptions);
  const captionsOpen = useUIStore((state) => state.captionsOpen);
  const setCurrentCaptions = useUIStore((state) => state.setCurrentCaptions);
  const setCaptionsFrozen = useUIStore((state) => state.setCaptionsFrozen);
  const setCaptionsExpanded = useUIStore((state) => state.setCaptionsExpanded);

  const chatWindowOpen = useChatSettingsStore((state) => state.chatWindowOpen);
  const hasHiddenCaption =
    !!lastAgentResponse &&
    !currentCaptions?.length &&
    captionsOpen &&
    !chatWindowOpen;
  const isStudentReview = useNotepadStore(selectIsNotepadNonInteractive);
  const isNotepadBite = useNotepadStore((s) => s.isNotepadBite);
  const notepadState = useNotepadStore((s) => s.state);

  const { showToast: showNameToast, dismiss: dismissNameToast } =
    useNameMentionDetector();
  const [showQuickNamePronunciation, setShowQuickNamePronunciation] =
    useState(false);
  const handleNameFix = useCallback(() => {
    onNamePronunciationPause?.();
    dismissNameToast();
    setShowQuickNamePronunciation(true);
  }, [dismissNameToast, onNamePronunciationPause]);
  const nameToastVisible =
    showNameToast &&
    notepadState !== "TOAST_VISIBLE" &&
    notepadState !== "CLOSING_COUNTDOWN";

  // Blob overlay positioning via direct DOM manipulation (no setState → no re-render loop)
  const sharePanelElRef = useRef(null);
  const blobWrapperRef = useRef(null);

  const syncBlobPosition = useCallback(() => {
    const placeholder = sharePanelElRef.current;
    const wrapper = blobWrapperRef.current;
    if (!placeholder || !wrapper) return;
    const rect = placeholder.getBoundingClientRect();
    const cssScale = minimizedRef.current ? 0.5 : 1;
    const w = rect.width * cssScale;
    const h = rect.height * cssScale;
    wrapper.style.position = "fixed";
    wrapper.style.top = `${rect.top}px`;
    wrapper.style.left = `${rect.right - w}px`;
    wrapper.style.width = `${w}px`;
    wrapper.style.height = `${h}px`;
    wrapper.style.zIndex = "1000";
    wrapper.style.pointerEvents = "auto";
  }, []);

  const handleSharePanelRef = useCallback(
    (el) => {
      sharePanelElRef.current = el;
      if (el) syncBlobPosition();
    },
    [syncBlobPosition],
  );

  // rAF loop — only while canvas is open, writes directly to DOM, no setState
  // On cleanup (canvas closes): revert fixed back to absolute so blob stays top-right
  useEffect(() => {
    const wrapper = blobWrapperRef.current;
    if (!isCanvasOpen) {
      if (wrapper) {
        wrapper.style.position = "absolute";
        wrapper.style.top = "10px";
        wrapper.style.left = "";
        wrapper.style.width = "";
        wrapper.style.height = "";
        wrapper.style.pointerEvents = "";
      }
      return;
    }
    let raf;
    const loop = () => {
      syncBlobPosition();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      if (wrapper) {
        wrapper.style.position = "absolute";
        wrapper.style.top = "10px";
        wrapper.style.left = "";
        wrapper.style.width = "";
        wrapper.style.height = "";
        wrapper.style.pointerEvents = "";
      }
    };
  }, [isCanvasOpen, syncBlobPosition]);

  // Morph blob into speech bubble when captions are hidden
  useEffect(() => {
    const ctrl = window.animationController;
    if (!ctrl) return;
    if (hasHiddenCaption) {
      ctrl.call("IDLE_TO_MESSAGE");
    } else {
      ctrl.exitMessageState();
    }
  }, [hasHiddenCaption]);

  const handleBlobClick = () => {
    if (!hasHiddenCaption) return;
    const { captionsSnapshots } = useUIStore.getState();
    const words =
      captionsSnapshots?.length > 0
        ? captionsSnapshots[captionsSnapshots.length - 1]
        : (lastAgentResponse?.split(" ").filter(Boolean) ?? []);
    if (words.length > 0) {
      setCurrentCaptions(words);
      setCaptionsFrozen(true);
      setCaptionsExpanded(false);
    }
    trackStudentInteraction({ type: StudentInteractionTypes.BLOB_CLICKED });
  };

  const handleMinimize = (e) => {
    e.stopPropagation();
    setMinimized(!minimized);
  };

  return (
    <div className="w-full h-full grid grid-rows-1 grid-cols-1 relative overflow-hidden">
      <div
        className="col-span-full row-span-full relative z-0 flex flex-row gap-4 min-h-0 items-stretch"
        style={{
          margin: 12,
          marginRight: isMobileLandscape ? 0 : 12,
          marginBottom: isMobilePortrait ? 0 : 12,
        }}
      >
        <div id="main-screen" className="flex-1 flex flex-col relative min-h-0">
          <div
            className={`flex flex-1 h-full mr-0 bg-white backdrop-blur-sm rounded-2xl shadow-lg scrollbar-hidden overflow-hidden min-h-0 ${isCanvasOpen ? "pointer-events-none" : ""}`}
          >
            <div className="flex-1 min-h-0">
              <div className="w-full h-full relative min-h-0">
                <Slide />
              </div>
            </div>
          </div>

          {/* Single persistent blob — outside overflow-hidden so fixed positioning works correctly.
              When canvas closed: absolute top-right of #main-screen.
              When canvas open: rAF loop repositions to SharePanel placeholder via fixed+getBCR.
              Hidden in student review mode (no AI agent active). */}
          <div
            ref={blobWrapperRef}
            id="blob-click-area"
            className="cursor-pointer relative"
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              zIndex: 350,
              display: isStudentReview ? "none" : "flex",
              justifyContent: "flex-end",
              alignItems: "flex-start",
              // overflow:hidden only when minimized — clips inner div and restricts click area.
              // Never apply to un-minimized state (avoids flex-shrink side-effects on mobile).
              ...(minimized && { overflow: "hidden" }),
              // Non-canvas: explicit size so click area matches visual blob.
              // Canvas-open: syncBlobPosition handles size directly in DOM.
              ...(!isCanvasOpen && {
                width: BLOB_W * blobScale * (minimized ? 0.5 : 1),
                height: BLOB_H * blobScale * (minimized ? 0.5 : 1),
                transition: "width 0.2s ease, height 0.2s ease",
              }),
            }}
          >
            <button
              style={{
                position: "relative",
                border: 0,
                padding: 0,
                background: "transparent",
                display: "inline-block",
                lineHeight: 0,
                transform: minimized ? "scale(0.5)" : undefined,
                transformOrigin: "top right",
                transition: "transform 0.2s ease",
              }}
              type="button"
              disabled={!hasHiddenCaption}
              aria-label="Show tutor captions"
              onClick={handleBlobClick}
            >
              <AudioVisualizer
                h={BLOB_H}
                w={BLOB_W}
                scale={blobScale}
                isEnded={sessionEnded}
                currentScreen={currentScreen}
              />
            </button>
            {!minimized && <ChannelToggles />}
            {!isMobileAny && (
              <button
                onClick={handleMinimize}
                style={{ position: "absolute", top: 0, right: 0, zIndex: 1001 }}
                className="w-8 h-8 flex items-center justify-center rounded-full text-white transition-colors"
                aria-label={minimized ? "Expand" : "Minimize"}
              >
                {minimized ? (
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 1H11V4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M7 5L11 1"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M4 11H1V8"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M5 7L1 11"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11 1L7 5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M10 5H7V2"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1 11L5 7"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M2 7H5V10"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            )}
          </div>

          {CANVAS_GATEWAY_CONFIGURED && (
            <OpenNotepadToast
              blobBottom={10 + BLOB_H * effectiveBlobScale + 8}
            />
          )}
          {CANVAS_GATEWAY_CONFIGURED && (
            <CloseNotepadToast
              blobBottom={10 + BLOB_H * effectiveBlobScale + 8}
            />
          )}

          <MicSilenceToast
            blobBottom={10 + BLOB_H * effectiveBlobScale + 8}
            onDeviceConfirmed={onMicDeviceConfirmed}
          />

          <FixNamePronunciationToast
            visible={nameToastVisible}
            blobBottom={10 + BLOB_H * effectiveBlobScale + 8}
            onFix={handleNameFix}
            onClose={dismissNameToast}
          />
          <QuickNamePronunciationModal
            isOpen={showQuickNamePronunciation}
            onClose={() => {
              setShowQuickNamePronunciation(false);
              onNamePronunciationResume?.();
            }}
            sessionId={sessionId}
            lessonId={currentLessonId}
          />

          {mode === TutorMode.PUBLIC && (!isCanvasOpen || isNotepadBite) && (
            <>
              <div className="absolute bottom-5 right-5 bg-transparent z-50">
                {slides && hasNextContentSlide(slides, currentSlideIndex) && (
                  <NextBiteButton />
                )}
              </div>
              <div className="absolute bottom-5 left-5 bg-transparent z-50">
                {currentSlideIndex > 0 && <PreviousBiteButton />}
              </div>
            </>
          )}
        </div>
        {isMobileAny && (
          <div
            className={`absolute inset-0 z-[400] flex flex-col ${isCanvasOpen ? "pointer-events-none" : ""}`}
            style={{ display: isScratchPadOpen ? "flex" : "none" }}
          >
            <ScratchPadPanel
              onPendingWorkChange={onScratchpadPendingChange}
              submitRef={scratchpadSubmitRef}
              resetPendingRef={scratchpadResetPendingRef}
              canvasExportRef={scratchpadCanvasExportRef}
              imageItemsRef={scratchpadImageItemsRef}
              clearImageBytesRef={scratchpadClearImageBytesRef}
              removeImageByIdRef={scratchpadRemoveImageByIdRef}
              onInteract={onScratchpadInteract}
              onCameraClick={onCameraClick}
              pendingImageB64={pendingImageB64}
              onImagePlaced={onImagePlaced}
              onImageCountChange={onScratchpadImageCountChange}
            />
          </div>
        )}
        <LazyMotion features={domAnimation}>
          <AnimatePresence>
            {!isMobileAny && isScratchPadOpen && (
              <m.div
                initial={{ x: "100%", opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                style={{ width: SCRATCHPAD_WIDTH }}
                exit={{ x: "100%", opacity: 0 }}
                transition={{
                  duration: 0.3,
                  ease: "easeInOut",
                  opacity: { duration: 0.2 },
                }}
                className={`shrink-0 h-full min-h-0 flex flex-col ${isCanvasOpen ? "pointer-events-none" : ""}`}
              >
                <ScratchPadPanel
                  onPendingWorkChange={onScratchpadPendingChange}
                  submitRef={scratchpadSubmitRef}
                  resetPendingRef={scratchpadResetPendingRef}
                  canvasExportRef={scratchpadCanvasExportRef}
                  imageItemsRef={scratchpadImageItemsRef}
                  removeImageByIdRef={scratchpadRemoveImageByIdRef}
                  onInteract={onScratchpadInteract}
                  onCameraClick={onCameraClick}
                  pendingImageB64={pendingImageB64}
                  onImagePlaced={onImagePlaced}
                  onImageCountChange={onScratchpadImageCountChange}
                />
              </m.div>
            )}
          </AnimatePresence>
        </LazyMotion>
        <NotificationStack
          stack={NOTIFICATION_STACKS[NotificationStackId.NEXT_SLIDE]}
        />
        <NotificationStack
          stack={NOTIFICATION_STACKS[NotificationStackId.TOP_CENTER]}
        />
        {canvasProps && (
          <TldrawCanvasPanel
            isOpen={isCanvasOpen}
            {...canvasProps}
            onSharePanelRef={handleSharePanelRef}
            blobScale={blobScale}
          />
        )}
      </div>
    </div>
  );
};

export default Screen;
